/**
 * BoxplotRenderer — distribution shape via pre-aggregated percentiles.
 *
 * ECharts boxplot consumes [min, Q1, median, Q3, max]. Our speed-to-contract
 * and contract-duration endpoints already return p25/p50/p75/p90 directly,
 * so we map [p25, p25, p50, p75, p90] (the bottom whisker collapses since we
 * don't have a real min — see the M-optional `p10_solicitation_to_award`
 * micro-ask if we want a real lower whisker).
 *
 * Replaces the legacy SpeedToContractSection / ContractDurationSection
 * Recharts components in Phase B3 (their internals will use this renderer).
 *
 * Phase C3 of issue #90 v3 plan.
 */
import { useMemo } from 'react'
import ReactECharts from 'echarts-for-react'
import type { PerfRow, GroupByDimension } from './types'
import { getDimensionLabel } from './types'
import { useExecChartTheme } from './echarts-theme'

interface Props {
  data: PerfRow[]
  /** Categorical dimension (X axis) — typically `fiscal_year` or `macom`. */
  groupBy: GroupByDimension
  /**
   * Percentile field names to read off each row, in order
   * [low_whisker, Q1, median, Q3, high_whisker]. Defaults to the
   * speed-to-contract shape: [p25, p25, p50, p75, p90].
   */
  percentileFields?: [string, string, string, string, string]
  /** Y-axis label. */
  axisLabel?: string
  /** Format function for tooltip values. Default: round + 'd' (days). */
  formatValue?: (v: number) => string
}

const DEFAULT_FIELDS: [string, string, string, string, string] = [
  'p25_solicitation_to_award',
  'p25_solicitation_to_award',
  'p50_solicitation_to_award',
  'p75_solicitation_to_award',
  'p90_solicitation_to_award',
]

export default function BoxplotRenderer({
  data,
  groupBy,
  percentileFields = DEFAULT_FIELDS,
  axisLabel = 'Days',
  formatValue = (v: number) => `${Math.round(v)}d`,
}: Props) {
  const theme = useExecChartTheme()

  const { categories, values } = useMemo(() => {
    const cats: string[] = []
    const vals: number[][] = []
    for (const row of data) {
      const point: number[] = []
      let valid = true
      for (const f of percentileFields) {
        const v = row[f]
        const num = typeof v === 'number' ? v : parseFloat(String(v))
        if (Number.isNaN(num)) { valid = false; break }
        point.push(num)
      }
      if (!valid) continue
      cats.push(getDimensionLabel(row, groupBy))
      vals.push(point)
    }
    return { categories: cats, values: vals }
  }, [data, percentileFields, groupBy])

  if (categories.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-gray-400">
        No distribution data
      </div>
    )
  }

  const option: any = {
    tooltip: {
      trigger: 'item',
      axisPointer: { type: 'shadow' },
      formatter: (params: any) => {
        const v = params?.data
        if (!Array.isArray(v) || v.length < 5) return ''
        const [, q1, median, q3, max] = v
        return [
          `<b>${params.name}</b>`,
          `p25: <b>${formatValue(q1)}</b>`,
          `p50 (median): <b>${formatValue(median)}</b>`,
          `p75: <b>${formatValue(q3)}</b>`,
          `p90: <b>${formatValue(max)}</b>`,
        ].join('<br/>')
      },
    },
    grid: { left: 60, right: 20, top: 25, bottom: 40 },
    xAxis: {
      type: 'category',
      data: categories,
      axisLabel: { fontSize: 10, rotate: categories.length > 8 ? 45 : 0 },
    },
    yAxis: {
      type: 'value',
      name: axisLabel,
      nameTextStyle: { fontSize: 10 },
      axisLabel: { fontSize: 10, formatter: (v: number) => formatValue(v) },
    },
    series: [
      {
        type: 'boxplot',
        data: values,
        boxWidth: ['20%', '40%'] as any,
      },
    ],
  }

  return (
    <ReactECharts
      option={option}
      theme={theme}
      style={{ height: '100%', width: '100%' }}
      opts={{ renderer: 'canvas' }}
    />
  )
}
